// Twilio Voice (browser calling) for the Power Dialer auto-dialer.
// OFF unless the server says so: /api/twilio-voice-token returns { enabled:false } when the
// TWILIO_VOICE_ENABLED flag is off or credentials are missing, and the dialer stays in tel: one-tap mode.
import { useCallback, useEffect, useRef, useState } from 'react';

export const AUTO_DIAL_COUNTDOWN_SECS = 3;
export const fmtDuration = (secs) => { const s = Math.max(0, Math.round(secs || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
const e164 = (raw) => { const d = String(raw || '').replace(/\D/g, ''); return d.length === 10 ? `+1${d}` : (d.length === 11 && d[0] === '1' ? `+${d}` : null); };

async function fetchToken() {
  const { auth } = await import('../../firebaseConfig');
  if (!auth.currentUser) return { enabled: false };
  const idToken = await auth.currentUser.getIdToken();
  const res = await fetch('/api/twilio-voice-token', { headers: { Authorization: `Bearer ${idToken}` } });
  if (!res.ok) return { enabled: false };
  return res.json().catch(() => ({ enabled: false }));
}

// status: 'off' | 'loading' | 'ready' | 'ringing' | 'in-call' | 'error'
export function useTwilioVoice(uid) {
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [callerId, setCallerId] = useState('');
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const [startedAt, setStartedAt] = useState(null); // answer time, for the live timer

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const t = await fetchToken();
        if (dead) return;
        if (!t.enabled || !t.token) { setStatus('off'); return; }
        const { Device } = await import('@twilio/voice-sdk');
        if (dead) return;
        const device = new Device(t.token, { codecPreferences: ['opus', 'pcmu'], closeProtection: true });
        device.on('tokenWillExpire', async () => { try { const n = await fetchToken(); if (n.token) device.updateToken(n.token); } catch (e) { /* keep going */ } });
        device.on('error', (e) => setError(e?.message || 'Calling error'));
        deviceRef.current = device;
        setCallerId(t.callerId || '');
        setStatus('ready');
      } catch (e) { if (!dead) { setStatus('off'); console.error('Twilio voice unavailable', e?.message); } }
    })();
    return () => { dead = true; try { callRef.current?.disconnect(); deviceRef.current?.destroy(); } catch (e) { /* ignore */ } deviceRef.current = null; };
  }, [uid]);

  // Places a call; onEnd({ answered, durationSecs, reason }) fires once when it ends for any reason.
  const dial = useCallback(async (contact, onEnd) => {
    const device = deviceRef.current; const To = e164(contact?.phone);
    if (!device || !To) { onEnd?.({ answered: false, durationSecs: 0, reason: 'not_placed' }); return; }
    setError(''); setStatus('ringing');
    let answeredAt = null; let done = false;
    const finish = (reason) => {
      if (done) return; done = true; callRef.current = null; setStartedAt(null); setStatus('ready');
      onEnd?.({ answered: !!answeredAt, durationSecs: answeredAt ? (Date.now() - answeredAt) / 1000 : 0, reason });
    };
    try {
      const call = await device.connect({ params: { To, contactId: contact.id } });
      callRef.current = call;
      call.on('accept', () => { answeredAt = Date.now(); setStartedAt(answeredAt); setStatus('in-call'); });
      call.on('disconnect', () => finish(answeredAt ? 'hangup' : 'no_answer'));
      call.on('cancel', () => finish('cancel'));
      call.on('reject', () => finish('reject'));
      call.on('error', (e) => { setError(e?.message || 'Call failed'); finish('error'); });
    } catch (e) { setError(e?.message || 'Call failed'); finish('error'); }
  }, []);
  const hangup = useCallback(() => { try { callRef.current?.disconnect(); } catch (e) { /* ignore */ } }, []);
  const mute = useCallback((on) => { try { callRef.current?.mute(on); } catch (e) { /* ignore */ } }, []);
  // Voicemail drop: stub for later (needs a recorded message + a server-side <Play> leg).
  const dropVoicemail = useCallback(() => { throw new Error('Voicemail drop is not available yet.'); }, []);

  return { enabled: status !== 'off' && status !== 'loading', status, error, callerId, startedAt, dial, hangup, mute, dropVoicemail };
}
